import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, Copy, ExternalLink, PackageCheck, Receipt } from 'lucide-react'
import { confirmOrderReceived, getMyOrders, type Order, type OrderShipping, type ShippingStatus } from '@/services/orderService'

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'รอชำระเงิน',
  PAID: 'ชำระเงินแล้ว',
  FAILED: 'ชำระเงินไม่สำเร็จ',
  REFUNDED: 'คืนเงินแล้ว',
}

const STATUS_PILL: Record<string, string> = {
  PENDING: 'bg-amber-50 text-amber-700',
  PAID: 'bg-emerald-50 text-emerald-700',
  FAILED: 'bg-rose-50 text-rose-600',
  REFUNDED: 'bg-[#F1F0F7] text-[#69738A]',
}

const PAYMENT_LABEL: Record<string, string> = {
  ONLINE: 'ชำระเงินออนไลน์',
  FREE: 'ฟรี',
}

// สถานะจัดส่งหนังสือ (ฝั่งลูกค้า) — ข้อความต่างจากฝั่งแอดมิน ใช้ภาษาที่ลูกค้าเข้าใจ
const SHIPPING_STEPS: { status: ShippingStatus; label: string }[] = [
  { status: 'PENDING', label: 'รอร้านรับออเดอร์' },
  { status: 'PREPARING', label: 'กำลังเตรียมสินค้า' },
  { status: 'SHIPPED', label: 'จัดส่งแล้ว' },
  { status: 'DELIVERED', label: 'ได้รับสินค้าแล้ว' },
]

// ลิงก์หน้าติดตามพัสดุของขนส่ง — แอดมินกรอกชื่อขนส่งเป็นข้อความอิสระ จึงเดาจากคำในชื่อ
// ไม่รู้จักขนส่ง = ใช้ 17TRACK (เว็บติดตามรวมหลายขนส่ง) แทน
const CARRIER_TRACKING: { match: RegExp; url: (n: string) => string }[] = [
  { match: /flash|แฟลช/i, url: (n) => `https://www.flashexpress.co.th/tracking/?se=${n}` },
  { match: /kerry|เคอรี่/i, url: (n) => `https://th.kerryexpress.com/th/track/?track=${n}` },
  { match: /thailand\s*post|ไปรษณีย์|ems/i, url: (n) => `https://track.thailandpost.co.th/?trackNumber=${n}` },
  { match: /dhl/i, url: (n) => `https://www.dhl.com/th-th/home/tracking.html?tracking-id=${n}` },
  { match: /ninja|นินจา/i, url: (n) => `https://www.ninjavan.co/th-th/tracking?id=${n}` },
]

function trackingUrl(carrier: string | null, trackingNumber: string): string {
  const n = encodeURIComponent(trackingNumber.trim())
  const found = carrier ? CARRIER_TRACKING.find((c) => c.match.test(carrier)) : undefined
  return found ? found.url(n) : `https://t.17track.net/th#nums=${n}`
}

function ShippingBlock({ orderId, shipping, onConfirmed }: { orderId: number; shipping: OrderShipping; onConfirmed: (order: Order) => void }) {
  const [copied, setCopied] = useState(false)
  const [asking, setAsking] = useState(false)      // กดครั้งแรกแล้วถามยืนยันอีกชั้น กันกดพลาด
  const [confirming, setConfirming] = useState(false)
  const [confirmError, setConfirmError] = useState('')
  const cancelled = shipping.shipping_status === 'CANCELLED'
  const stepIndex = SHIPPING_STEPS.findIndex((s) => s.status === shipping.shipping_status)

  const copyTracking = () => {
    if (!shipping.tracking_number) return
    navigator.clipboard?.writeText(shipping.tracking_number).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }).catch(() => {})
  }

  const confirmReceived = async () => {
    setConfirming(true)
    setConfirmError('')
    try {
      onConfirmed(await confirmOrderReceived(orderId))
    } catch (err) {
      setConfirmError(err instanceof Error ? err.message : 'ยืนยันไม่สำเร็จ')
    } finally {
      setConfirming(false)
      setAsking(false)
    }
  }

  return (
    <div className="mt-3 border-t border-[#F0EEFB] pt-3">
      <div className="mb-3 flex items-center gap-1.5 text-xs font-medium text-[#19172A]">
        <PackageCheck className="h-4 w-4 text-accent" /> การจัดส่ง · ส่งฟรี
      </div>

      {cancelled ? (
        <p className="mb-3 rounded-sm bg-rose-50 px-3 py-2 text-xs text-rose-600">การจัดส่งนี้ถูกยกเลิก หากมีข้อสงสัยกรุณาติดต่อทีมงาน</p>
      ) : (
        <ol className="mb-3 flex items-start">
          {SHIPPING_STEPS.map((step, i) => {
            const done = i <= stepIndex
            return (
              <li key={step.status} className="flex flex-1 flex-col items-center text-center">
                <div className="flex w-full items-center">
                  <span className={`h-0.5 flex-1 ${i === 0 ? 'bg-transparent' : i <= stepIndex ? 'bg-accent' : 'bg-[#E7E7ED]'}`} />
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${done ? 'bg-accent text-white' : 'bg-[#F1F0F7] text-[#9AA1B2]'}`}>
                    {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
                  </span>
                  <span className={`h-0.5 flex-1 ${i === SHIPPING_STEPS.length - 1 ? 'bg-transparent' : i < stepIndex ? 'bg-accent' : 'bg-[#E7E7ED]'}`} />
                </div>
                <span className={`mt-1.5 px-1 text-[11px] leading-tight ${i === stepIndex ? 'font-semibold text-[#19172A]' : 'text-[#9AA1B2]'}`}>{step.label}</span>
              </li>
            )
          })}
        </ol>
      )}

      {shipping.tracking_number && (
        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-sm bg-[#F7F6FF] px-3 py-2 text-xs">
          <span className="text-[#69738A]">{shipping.carrier || 'เลขพัสดุ'}</span>
          <span className="font-semibold tracking-wide text-[#19172A]">{shipping.tracking_number}</span>
          <button type="button" onClick={copyTracking} className="inline-flex items-center gap-1 text-accent hover:underline">
            <Copy className="h-3 w-3" /> {copied ? 'คัดลอกแล้ว' : 'คัดลอก'}
          </button>
          <a
            href={trackingUrl(shipping.carrier, shipping.tracking_number)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-accent hover:underline"
          >
            <ExternalLink className="h-3 w-3" /> ติดตามพัสดุ
          </a>
        </div>
      )}

      {shipping.shipping_status === 'SHIPPED' && (
        <div className="mb-3 rounded-sm border border-accent/30 bg-[#F7F6FF] px-3 py-2.5">
          {!asking ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-[#69738A]">ได้รับหนังสือเรียบร้อยแล้วใช่ไหม?</span>
              <button type="button" onClick={() => setAsking(true)} className="rounded-sm bg-accent px-3.5 py-1.5 text-xs font-semibold text-white transition hover:opacity-90">
                ยืนยันว่าได้รับสินค้าแล้ว
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs font-medium text-[#19172A]">ยืนยันว่าคุณได้รับหนังสือครบถ้วนแล้ว?</span>
              <span className="flex gap-2">
                <button type="button" disabled={confirming} onClick={() => setAsking(false)} className="rounded-sm border border-[#E2E4EA] px-3 py-1.5 text-xs text-[#69738A] hover:border-accent hover:text-accent">ยังไม่ได้รับ</button>
                <button type="button" disabled={confirming} onClick={confirmReceived} className="rounded-sm bg-emerald-600 px-3.5 py-1.5 text-xs font-semibold text-white transition hover:opacity-90 disabled:opacity-60">
                  {confirming ? 'กำลังบันทึก...' : 'ยืนยัน ได้รับแล้ว'}
                </button>
              </span>
            </div>
          )}
          {confirmError && <p className="mt-2 text-xs text-rose-500">{confirmError}</p>}
        </div>
      )}

      <p className="text-xs leading-relaxed text-[#69738A]">
        จัดส่งถึง {shipping.recipient_name} · {shipping.phone}<br />
        {shipping.address} {shipping.subdistrict} {shipping.district} {shipping.province} {shipping.postal_code}
        {shipping.shipped_at && <><br />ส่งออกเมื่อ {formatDate(shipping.shipped_at)}</>}
        {shipping.delivered_at && <> · ได้รับเมื่อ {formatDate(shipping.delivered_at)}</>}
      </p>
    </div>
  )
}

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })

export default function OrdersTab() {
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    getMyOrders()
      .then(setOrders)
      .catch((err) => setError(err instanceof Error ? err.message : 'โหลดประวัติคำสั่งซื้อไม่สำเร็จ'))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div>
      <p className="mb-6 text-sm text-[#69738A]">ประวัติคำสั่งซื้อทั้งหมดของคุณ ({orders.length})</p>

      {loading ? (
        <p className="text-sm text-[#69738A]">กำลังโหลด...</p>
      ) : error ? (
        <p className="text-sm text-rose-500">{error}</p>
      ) : orders.length === 0 ? (
        <div className="border border-[#E7E7ED] bg-white p-12 text-center" style={{ borderRadius: 6 }}>
          <p className="text-[#69738A]">คุณยังไม่มีประวัติคำสั่งซื้อ</p>
        </div>
      ) : (
        <div className="divide-y divide-[#E7E7ED] border border-[#E7E7ED] bg-white" style={{ borderRadius: 6 }}>
          {orders.map((order) => (
            <div key={order.order_id} className="flex gap-5 p-5 transition-colors hover:bg-[#FAFAFF]">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center bg-[#F0EDFF]" style={{ borderRadius: 4 }}>
                <Receipt className="h-5 w-5 text-accent/60" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_PILL[order.status] || 'bg-[#F1F0F7] text-[#69738A]'}`}>
                    {STATUS_LABEL[order.status] || order.status}
                  </span>
                  <span className="text-xs text-[#9AA1B2]">{order.order_number}</span>
                </div>
                <p className="mb-1 text-[15px] font-medium leading-snug text-[#19172A]">
                  {order.items.map((item) => item.quantity > 1 ? `${item.item_name} × ${item.quantity}` : item.item_name).join(', ')}
                </p>
                <p className="text-xs text-[#69738A]">
                  {formatDate(order.created_date)}
                  {order.payment && ` · ${PAYMENT_LABEL[order.payment.payment_method] || order.payment.payment_method}`}
                </p>
                {/* รายชื่อผู้ได้ที่นั่ง/คอร์ส — Onsite: ผู้เข้าอบรม, ออนไลน์ที่ซื้อให้พนักงาน: อีเมลที่ได้คอร์ส (ไม่แสดงถ้าซื้อให้ตัวเอง) */}
                {order.items.map((item) => {
                  const people = item.participants.filter((p) => !p.is_self)
                  if (order.status !== 'PAID' || people.length === 0) return null
                  return (
                    <ul key={item.order_item_id} className="mt-3 space-y-1 border-t border-[#F0EEFB] pt-2.5 text-xs text-[#69738A]">
                      {people.map((p) => (
                        <li key={p.email} className="flex items-center justify-between gap-2">
                          <span>{p.first_name ? `${p.first_name} ${p.last_name ?? ''}`.trim() : p.email}</span>
                          <span className="shrink-0 text-[#9AA1B2]">{p.email}{p.has_account ? '' : ' · ยังไม่มีบัญชี'}</span>
                        </li>
                      ))}
                    </ul>
                  )
                })}
                {order.shipping && order.status === 'PAID' && (
                  <ShippingBlock
                    orderId={order.order_id}
                    shipping={order.shipping}
                    onConfirmed={(updated) => setOrders((prev) => prev.map((o) => (o.order_id === updated.order_id ? updated : o)))}
                  />
                )}
              </div>
              <div className="shrink-0 text-right">
                <p className="text-[15px] font-semibold text-[#19172A]">
                  {order.final_amount === 0 ? 'ฟรี' : `฿${order.final_amount.toLocaleString()}`}
                </p>
                {order.coupon_code && order.discount > 0 && (
                  <p className="mt-0.5 text-xs text-emerald-600">คูปอง {order.coupon_code} (-฿{order.discount.toLocaleString()})</p>
                )}
                {/* ออเดอร์ที่ยังรอชำระเงิน — กดไปหน้าตรวจสอบการชำระเงินเพื่อให้ระบบถามผลกับ Gateway อีกรอบ */}
                {order.status === 'PENDING' && (
                  <Link
                    to={`/payment/result/${order.order_id}`}
                    className="mt-2 inline-block border border-[#E2E4EA] px-3 py-1.5 text-xs font-medium text-[#19172A] transition-colors hover:border-accent hover:text-accent"
                  >
                    ตรวจสอบการชำระเงิน
                  </Link>
                )}
                {/* ปุ่มดูใบกำกับภาษี — แสดงเฉพาะคำสั่งซื้อที่ขอใบกำกับภาษีตอนชำระเงิน (มี billing_info); ไม่ได้ขอ = ไม่มีปุ่ม */}
                {order.status === 'PAID' && order.final_amount > 0 && order.billing_info && (
                  <Link
                    to={`/receipt/${order.order_id}`}
                    className="mt-2 inline-block border border-[#E2E4EA] px-3 py-1.5 text-xs font-medium text-[#19172A] transition-colors hover:border-accent hover:text-accent"
                  >
                    ดูใบกำกับภาษี
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
